import { useState } from 'react'
import { Drawer, DrawerField, DrawerSection } from '@/shared/ui/Drawer'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import { fmtDateTime } from '@/shared/lib/format'
import { deleteCandidate, getCandidate } from './api'
import { CandidateFormDialog } from './CandidateFormDialog'
import {
  EntityChip, Pill, candidateStatusLabel, candidateStatusTone, seatScopeSentence, seatTypeLabel,
} from './vocab'

/**
 * One candidate.
 *
 * ## Delete is offered, and the server refuses the ones that matter
 *
 * An ELECTED candidate is answered with 409 `politics/candidate-elected`: deleting
 * them would remove them from every poll that ever asked about them, and the
 * refusal names the alternative (set the status to WITHDRAWN). That sentence is
 * rendered as sent. Hiding the button for elected candidates would leave somebody
 * looking for a control that is not there, with nothing on screen explaining why —
 * and it would hide the alternative too.
 *
 * ## Derived coalitions say so
 *
 * `coalitionDerived` distinguishes "in the alliance because their party is" from
 * "in the alliance themselves". The same chip for both would misstate one of them.
 */
export function CandidateDrawer({ candidateId, onClose, onChanged, onDeleted }: {
  candidateId: string | null
  onClose: () => void
  onChanged: () => void
  onDeleted: () => void
}) {
  const canWrite = useCan('politics:candidate')

  const candidate = useLoad(
    () => (candidateId ? getCandidate(candidateId) : Promise.resolve(null)), [candidateId])

  const [editing, setEditing] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const row = candidate.data

  async function remove() {
    if (!candidateId) return
    setBusy(true)
    setActionError(null)
    try {
      await deleteCandidate(candidateId)
      setConfirming(false)
      onDeleted()
    } catch (e) {
      setActionError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Drawer
        open={candidateId !== null}
        onClose={onClose}
        width="lg"
        title={row ? row.fullName : 'Candidate'}
        subtitle={row
          ? `${seatTypeLabel(row.seatTypeCode)} · ${row.unitName ?? 'no unit'}`
          : undefined}
        right={row?.incumbent
          ? <Pill tone="bg-chocolate-soft/60 text-chocolate border-chocolate/30">Incumbent</Pill>
          : undefined}
        footer={(
          <>
            {!canWrite && (
              <span className="text-erp-xs text-muted-foreground mr-auto">
                Editing and deletion need the <code>politics:candidate</code> authority.
              </span>
            )}
            <button type="button" className="erp-btn h-9 text-erp-sm" onClick={onClose}>Close</button>
            <button
              type="button"
              className="erp-btn h-9 text-erp-sm text-red-700"
              disabled={!row || !canWrite || busy}
              onClick={() => { setConfirming(true); setActionError(null) }}
            >
              Delete
            </button>
            <button type="button" className="erp-btn erp-btn-primary h-9 text-erp-sm"
              disabled={!row || !canWrite} onClick={() => setEditing(true)}>
              Edit
            </button>
          </>
        )}
      >
        <ErrorStrip message={candidate.error} />

        {candidate.loading && !row ? <LoadingBlock label="Loading the candidate…" /> : row && (
          <>
            {(confirming || actionError) && (
              <div className="p-4 space-y-2 border-b border-border">
                {actionError && <Note tone="bad">{actionError}</Note>}
                {confirming && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-erp-sm">
                      Delete {row.fullName}? Nothing that referenced them keeps a copy.
                    </span>
                    <span className="ml-auto flex items-center gap-2">
                      <button type="button" className="erp-btn-ghost h-8 text-erp-xs"
                        onClick={() => setConfirming(false)}>Cancel</button>
                      <button type="button" className="erp-btn h-8 text-erp-xs text-red-700"
                        disabled={busy} onClick={remove}>
                        {busy ? 'Deleting…' : 'Delete'}
                      </button>
                    </span>
                  </div>
                )}
              </div>
            )}

            <DrawerSection title="Identity">
              <DrawerField label="Name" value={row.fullName} />
              <DrawerField label="Status" value={(
                <Pill tone={candidateStatusTone(row.statusCode)}>
                  {candidateStatusLabel(row.statusCode)}
                </Pill>
              )} />
              <DrawerField label="Incumbent"
                value={row.incumbent ? 'Yes — holds this seat now' : 'No'} />
            </DrawerSection>

            <DrawerSection title="Seat">
              <DrawerField label="Seat type"
                value={`${seatTypeLabel(row.seatTypeCode)} (${row.seatTypeCode})`} />
              <DrawerField label="Scope rule"
                value={seatScopeSentence(row.seatTypeCode, row.requiredLevel)} />
              <DrawerField label="Unit" value={row.unitName ?? '—'} />
              <DrawerField
                label="Where"
                // The ancestors as the server sent them. Building this path on the
                // client would need the whole hierarchy loaded to say one sentence.
                value={row.unitLabels.length > 0 ? row.unitLabels.join(' › ') : '—'}
              />
              <DrawerField label="Unit level" value={row.unitTypeCode ?? '—'} />
            </DrawerSection>

            <DrawerSection title="Banner">
              <DrawerField
                label="Party"
                value={row.party
                  ? <EntityChip entity={{
                      code: row.party.code, name: row.party.name,
                      shortName: row.party.shortName, colourHex: row.party.colourHex,
                    }} />
                  : 'Independent — no party'}
              />
              <DrawerField
                label="Coalition"
                value={row.coalition
                  ? (
                    <span className="inline-flex flex-wrap items-center gap-2">
                      <EntityChip entity={row.coalition} />
                      <span className="text-erp-xs text-muted-foreground">
                        {row.coalitionDerived
                          ? `derived from ${row.party?.name ?? 'the party'}'s membership — it follows the party`
                          : 'set on the candidate directly — no party stands between them and it'}
                      </span>
                    </span>
                  )
                  : 'None'}
              />
            </DrawerSection>

            {row.notes && (
              <DrawerSection title="Notes">
                <div className="px-4 py-2">
                  <p className="text-erp-sm whitespace-pre-wrap">{row.notes}</p>
                </div>
              </DrawerSection>
            )}

            <DrawerSection title="Record">
              <DrawerField label="Created" value={fmtDateTime(row.createdAt)} />
              <DrawerField label="Updated" value={fmtDateTime(row.updatedAt)} />
              <DrawerField label="Version" value={String(row.version)} mono />
            </DrawerSection>
          </>
        )}
      </Drawer>

      {/* Mounted only while open — it loads three lookups when it mounts. */}
      {editing && (
        <CandidateFormDialog
          open={editing}
          candidate={row}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); candidate.reload(); onChanged() }}
        />
      )}
    </>
  )
}
